import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Gambling.css';

interface GamblingProps {
  isOpen: boolean;
  onClose: () => void;
  coins: number;
  onCoinsChange: (delta: number) => void;
  onSpinResult?: (result: 'win' | 'lose', payout: number) => void;
  autoplayUnlocked: boolean;
  coinsSpent: number;
  slotCount: number;
  onUpgradeSlots: () => void;
}

const SYMBOLS = ['🍒', '🍋', '🍇', '🍀', '⭐', '💎', '🔥'];
const SPIN_COST = 1;

const getWinReward = (slots: number): number => {
  // 3 slots: 50, 4 slots: 400, 5 slots: 5000
  if (slots === 3) return 50;
  if (slots === 4) return 400;
  if (slots === 5) return 5000;
  return 50;
};

const getUpgradeCost = (currentSlots: number): number => {
  // Upgrade to 4 slots: 1000 coins, to 5 slots: 10000 coins
  if (currentSlots === 3) return 1000;
  if (currentSlots === 4) return 10000;
  return 0;
};

const Gambling: React.FC<GamblingProps> = ({ isOpen, onClose, coins, onCoinsChange, onSpinResult, autoplayUnlocked, coinsSpent, slotCount, onUpgradeSlots }) => {
  const [reels, setReels] = useState<string[]>(() => Array(slotCount).fill('🍒'));
  const [isSpinning, setIsSpinning] = useState(false);
  const [message, setMessage] = useState<string>(() => {
    const reward = getWinReward(slotCount);
    return `Get ${slotCount} of a kind to win ${reward} coins!`;
  });

  // Update reels when slot count changes
  useEffect(() => {
    setReels(Array(slotCount).fill('🍒'));
    const reward = getWinReward(slotCount);
    setMessage(`Get ${slotCount} of a kind to win ${reward} coins!`);
  }, [slotCount]);
  const [autoplayEnabled, setAutoplayEnabled] = useState(false);
  const spinIntervalRef = useRef<number | null>(null);
  const autoplayTimeoutRef = useRef<number | null>(null);

  const getRandomSymbol = () => {
    const index = Math.floor(Math.random() * SYMBOLS.length);
    return SYMBOLS[index];
  };

  const handleSpin = useCallback(() => {
    if (isSpinning) return;
    if (coins < SPIN_COST) {
      setMessage('Not enough coins to spin!');
      return;
    }

    // Deduct spin cost up front
    onCoinsChange(-SPIN_COST);
    setIsSpinning(true);
    setMessage('Spinning...');

    // Rapidly change symbols for animation
    spinIntervalRef.current = window.setInterval(() => {
      setReels(Array(slotCount).fill(null).map(() => getRandomSymbol()));
    }, 80);

    // Stop after a short delay and determine result
    window.setTimeout(() => {
      if (spinIntervalRef.current !== null) {
        window.clearInterval(spinIntervalRef.current);
        spinIntervalRef.current = null;
      }

      const finalReels = Array(slotCount).fill(null).map(() => getRandomSymbol());
      setReels(finalReels);

      // Check if all reels match
      const isWin = finalReels.every(symbol => symbol === finalReels[0]);
      const winReward = getWinReward(slotCount);
      
      if (isWin) {
        onCoinsChange(winReward);
        setMessage(`JACKPOT! You won ${winReward} coins!`);
        if (onSpinResult) {
          onSpinResult('win', winReward);
        }
      } else {
        setMessage('No luck this time. Try again?');
        if (onSpinResult) {
          onSpinResult('lose', 0);
        }
      }

      setIsSpinning(false);
    }, 1200);
  }, [isSpinning, coins, onCoinsChange, onSpinResult, slotCount]);

  // Autoplay logic
  useEffect(() => {
    if (!autoplayEnabled) return;

    if (!isSpinning && coins >= SPIN_COST) {
      autoplayTimeoutRef.current = window.setTimeout(() => {
        handleSpin();
      }, 500);
    } else if (coins < SPIN_COST) {
      setAutoplayEnabled(false);
      setMessage('Autoplay stopped: Not enough coins!');
    }

    return () => {
      if (autoplayTimeoutRef.current !== null) {
        window.clearTimeout(autoplayTimeoutRef.current);
      }
    };
  }, [autoplayEnabled, isSpinning, coins, handleSpin]);

  useEffect(() => {
    return () => {
      if (spinIntervalRef.current !== null) {
        window.clearInterval(spinIntervalRef.current);
      }
      if (autoplayTimeoutRef.current !== null) {
        window.clearTimeout(autoplayTimeoutRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="gambling-overlay" onClick={onClose}>
      <div className="gambling-popup" onClick={(e) => e.stopPropagation()}>
        <button className="gambling-close-button" onClick={onClose}>×</button>

        <div className="gambling-header">
          <h2 className="gambling-title">Gambling Addiction</h2>
          <p className="gambling-subtitle">
            Each spin costs {SPIN_COST} coins. {slotCount} in a row wins {getWinReward(slotCount)} coins.
          </p>
          <p className="gambling-coins">Your coins: {coins.toLocaleString()} 🪙</p>
          {slotCount < 5 && (
            <div className="gambling-upgrade-section">
              <p className="gambling-upgrade-info">
                Upgrade to {slotCount + 1} slots for {getUpgradeCost(slotCount).toLocaleString()} 🪙
                <br />
                Win {getWinReward(slotCount + 1)} coins with {slotCount + 1} matching symbols!
              </p>
              <button
                className="gambling-upgrade-button"
                onClick={() => {
                  const cost = getUpgradeCost(slotCount);
                  if (coins >= cost) {
                    onCoinsChange(-cost);
                    onUpgradeSlots();
                    setMessage(`Upgraded to ${slotCount + 1} slots!`);
                  } else {
                    setMessage(`Not enough coins! Need ${cost.toLocaleString()} 🪙`);
                  }
                }}
                disabled={coins < getUpgradeCost(slotCount)}
              >
                Upgrade to {slotCount + 1} Slots ({getUpgradeCost(slotCount).toLocaleString()} 🪙)
              </button>
            </div>
          )}
        </div>

        <div className={`slot-machine ${isSpinning ? 'spinning' : ''}`}>
          <div className="slot-reels">
            {reels.map((symbol, index) => (
              <div key={index} className="slot-reel">
                <span className="slot-symbol">{symbol}</span>
              </div>
            ))}
          </div>
          <div className="slot-controls">
            <button
              className="slot-spin-button"
              onClick={handleSpin}
              disabled={isSpinning || coins < SPIN_COST || autoplayEnabled}
            >
              {isSpinning ? 'SPINNING...' : `SPIN (${SPIN_COST} 🪙)`}
            </button>
            {autoplayUnlocked && (
              <button
                className={`slot-autoplay-button ${autoplayEnabled ? 'active' : ''}`}
                onClick={() => {
                  if (coins >= SPIN_COST) {
                    setAutoplayEnabled(!autoplayEnabled);
                    setMessage(autoplayEnabled ? 'Autoplay stopped.' : 'Autoplay enabled!');
                  } else {
                    setMessage('Not enough coins for autoplay!');
                  }
                }}
                disabled={coins < SPIN_COST}
              >
                {autoplayEnabled ? '⏸️ STOP AUTOPLAY' : '▶️ AUTOPLAY'}
              </button>
            )}
            {!autoplayUnlocked && (
              <div className="autoplay-lock-info">
                <p>Spend {100 - coinsSpent} more coins to unlock autoplay</p>
              </div>
            )}
          </div>
        </div>

        <p className="gambling-message">{message}</p>
      </div>
    </div>
  );
};

export default Gambling;


